import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CalendarEmptyStateProps {
  kind: 'empty' | 'noChannel';
  /** "November", "19 – 25 Oct". */
  periodLabel: string;
  onOpenReleases: () => void;
  onNewPost: () => void;
  onOpenChannels: () => void;
}

/**
 * The empty states (design b9). With no channel: connect one first. With channels and no post in
 * the period: a next step, over the faint grid.
 */
const CalendarEmptyStateComponent: React.FC<CalendarEmptyStateProps> = ({
  kind,
  periodLabel,
  onOpenReleases,
  onNewPost,
  onOpenChannels,
}) => {
  const intl = useIntl();
  if (kind === 'noChannel') {
    return (
      <div className={styles.emptyc} data-testid="calendar-no-channel">
        <b>
          {intl.formatMessage({
            id: 'app.sml.promote.calendar.noChannel.title',
            defaultMessage: 'Connect a channel to plan posts.',
          })}
        </b>
        <p>
          {withBrandWord(
            intl.formatMessage({
              id: 'app.sml.promote.calendar.noChannel.body',
              defaultMessage:
                'Connect TikTok, Instagram, YouTube, Facebook, Threads or X. MUSIC24 posts for you where the platform lets it, and reminds you where it does not.',
            })
          )}
        </p>
        <SMLButton color="primary" omitCap onClick={onOpenChannels}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.openConnections', defaultMessage: 'Open Connections' })}
        </SMLButton>
      </div>
    );
  }
  return (
    <div className={styles.emptyc} data-testid="calendar-empty">
      <b>
        {intl.formatMessage(
          { id: 'app.sml.promote.calendar.empty.title', defaultMessage: 'Nothing planned in {period}.' },
          { period: periodLabel }
        )}
      </b>
      <p>
        {intl.formatMessage({
          id: 'app.sml.promote.calendar.empty.body',
          defaultMessage: 'Plan the rollout of a song on its page, or write one post.',
        })}
      </p>
      <div className="d-flex gap-2">
        <SMLButton color="primary" omitCap onClick={onOpenReleases}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.openReleases', defaultMessage: 'Open Releases' })}
        </SMLButton>
        <SMLButton outline omitCap onClick={onNewPost}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.newPost', defaultMessage: 'New post' })}
        </SMLButton>
      </div>
    </div>
  );
};

export const CalendarEmptyState = TypedMemo(CalendarEmptyStateComponent);
